import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, HelpCircle, FileText, Network, Quote } from 'lucide-react';

const geoQueries = ['"Best dentist near me"', '"Top HVAC companies in [city]"', '"Recommended financial advisers in [area]"'];
const aeoQueries = ['"How much does a dental implant cost?"', '"What causes a boiler to stop working?"', '"How do I know if I need a financial adviser?"'];

const services = [
  { icon: <HelpCircle className="w-6 h-6" />, title: 'FAQ & Q&A Content Strategy', desc: 'We identify the exact informational questions buyers ask in your client\'s category — by market, by vertical — and build a structured FAQ programme answered in the format AI engines prefer to cite.', items: ['Question research by vertical and market', 'Answer format optimisation for AI citation', 'Schema-marked FAQ page implementation', 'Monthly question set expansion'] },
  { icon: <FileText className="w-6 h-6" />, title: 'Authoritative Content Creation', desc: 'We create or optimise long-form content that establishes your client as an expert source — the kind of content AI engines pull from when answering category questions.', items: ['Topic authority mapping', 'Content written for AI citation format', "Published under your client's brand", 'Monthly content additions'] },
  { icon: <Network className="w-6 h-6" />, title: 'Entity Authority Building', desc: 'AI engines build a model of who is authoritative in a category. Entity authority is built through consistent mentions, cross-platform presence, and references from other authoritative sources.', items: ['Entity consistency audit', 'Third-party mention strategy', 'Industry directory and publication outreach', 'Wikipedia and Wikidata signals where applicable'] },
  { icon: <Quote className="w-6 h-6" />, title: 'AI Citation Source Tracking', desc: 'We track when and where your client is cited as a source in AI responses — not just whether they appear, but whether the AI is quoting them as an authority. Different metric. Higher value.', items: ['Citation source tracking monthly', 'Query-level citation breakdown', 'Competitor citation comparison', 'White-label monthly report'] }
];

const milestones = [
  { phase: 'Month 1–2', title: 'Foundation', desc: 'Audit complete. FAQ content live. Schema implemented. Baseline AI citation score established.', deliverable: 'AI Visibility Baseline Report' },
  { phase: 'Month 3–4', title: 'Momentum', desc: 'First citation improvements visible. AI engines beginning to recognise entity authority. Competitor gap analysis updated.', deliverable: 'Citation Improvement Report' },
  { phase: 'Month 6+', title: 'Compounding', desc: 'Client cited regularly for category queries. Content being pulled as source answers. Competitor visibility declining relative to client.', deliverable: 'Monthly AI Share of Voice' }
];

const faqs = [
  { q: 'How is AEO different from traditional SEO?', a: 'Traditional SEO optimises for appearing in a list of search results. AEO optimises for being quoted as a source inside an AI-generated answer. Different outcome, different signals, different strategy — though they complement each other. An AEO-optimised page will typically also perform better in traditional search.' },
  { q: 'How long does it take to see results?', a: 'First citation improvements typically appear within 60–90 days. Meaningful share of voice improvements take 3–6 months. AEO is a compounding service — the longer a client is on the programme, the harder it becomes for competitors to displace them.' },
  { q: 'Does this work outside the US?', a: 'Yes. AI engines serve users globally, and the informational queries they answer are relevant in every English-speaking market. We have specific content and entity strategies for the US, UK, Australia, Canada, and key European markets. Multi-language AEO is available on request.' },
  { q: "Can I offer this to clients who don't have a blog or content team?", a: "Yes — that's the point. MarqiRank creates the content. Your client doesn't need a writer, a content strategist, or a CMS team. We produce, optimise, and publish everything under their brand." }
];

const altBg = { background: 'rgba(74, 53, 128, 0.07)' };

const RankAeoPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rank-bg)' }} data-testid="rank-aeo-page">
      <Helmet><title>MarqiRank — Answer Engine Optimisation (AEO)</title></Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 text-center">
        <div className="container max-w-4xl">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="rank-aeo-hero-title">
            Your Clients Don't Just Need to Appear in AI.
            <br /><span className="text-[var(--rank-accent)]">They Need to Be the Source It Quotes.</span>
          </h1>
          <p className="body-lg mb-12 max-w-3xl mx-auto">
            Answer Engine Optimisation (AEO) is how your clients become the authoritative source that
            ChatGPT, Gemini, and Perplexity quote when someone asks an informational question — 'how much
            does a dental implant cost?' or 'what should I look for in an HVAC company?'. Being quoted is
            more valuable than being listed.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="rank-aeo-cta-primary">
              Get a Free AEO Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/rank/geo" className="btn-secondary">See GEO Instead</Link>
          </div>
        </div>
      </section>

      {/* GEO vs AEO */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">Two Different Queries. Two Different Strategies.</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="feature-card">
              <h3 className="h3 text-[var(--rank-accent)] mb-6">GEO Query Type</h3>
              <div className="space-y-3 mb-6">
                {geoQueries.map((q) => (<p key={q} className="text-[var(--text-secondary)] italic">{q}</p>))}
              </div>
              <p className="body-md">These are <strong className="text-[var(--text-primary)]">recommendation queries</strong>. The user wants a business name. GEO makes your client the business that appears. Strategy: presence signals — schema, citations, GBP, entity consistency.</p>
            </div>
            <div className="feature-card border-[var(--rank-primary)]">
              <h3 className="h3 text-[var(--rank-accent)] mb-6">AEO Query Type</h3>
              <div className="space-y-3 mb-6">
                {aeoQueries.map((q) => (<p key={q} className="text-[var(--text-secondary)] italic">{q}</p>))}
              </div>
              <p className="body-md">These are <strong className="text-[var(--text-primary)]">informational queries</strong>. The user wants an answer. AEO makes your client the source the AI credits for that answer. Strategy: content authority — FAQ pages, structured answers, entity expertise signals.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">What an AEO Engagement Includes.</h2>
            <p className="body-lg">AEO is a content and authority programme. It takes 60–90 days to show measurable citation improvements — but the results compound over time and are extremely difficult for competitors to replicate quickly.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((s, i) => (
              <div key={s.title} className="feature-card hover-lift animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-[var(--rank-accent)] mr-4" style={{ background: 'rgba(74,53,128,0.18)' }}>{s.icon}</div>
                  <h3 className="h3 text-[var(--text-primary)]">{s.title}</h3>
                </div>
                <p className="body-md mb-6">{s.desc}</p>
                <ul className="space-y-2">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-start space-x-3">
                      <Check className="w-4 h-4 text-[var(--rank-accent)] flex-shrink-0 mt-1" />
                      <span className="text-[var(--text-secondary)] text-sm">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why AEO compounds */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">AEO Gets Stronger Every Month.</h2>
            <p className="body-lg">Unlike paid ads that stop the moment you stop spending, AEO builds durable authority. Each piece of content, each citation signal, each structured answer compounds.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {milestones.map((m, i) => (
              <div key={m.phase} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="text-sm font-semibold text-[var(--rank-accent)] uppercase tracking-wide mb-2">{m.phase}</div>
                <h3 className="h2 text-[var(--text-primary)] mb-4">{m.title}</h3>
                <p className="body-md mb-6">{m.desc}</p>
                <div className="pt-4 border-t border-[var(--border-subtle)] text-sm text-[var(--text-muted)]"><span className="text-[var(--rank-accent)] font-semibold">Deliverable: </span>{m.deliverable}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs + CTA */}
      <section className="py-24">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16">Common Questions About AEO.</h2>
          <div className="max-w-4xl mx-auto space-y-6 mb-20">
            {faqs.map((f, i) => (
              <div key={f.q} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.08}s` }}>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{f.q}</h3>
                <p className="body-md">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="feature-card text-center max-w-4xl mx-auto" style={altBg}>
            <h2 className="h2 text-[var(--text-primary)] mb-6">Find Out If Your Client Is Being Cited in AI Answers.</h2>
            <p className="body-lg mb-12 max-w-2xl mx-auto">We'll run a free AEO audit — showing which informational queries in your client's category are producing AI answers, and whether your client is being cited as a source today.</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect">Get a Free AEO Audit<ArrowRight className="ml-2 w-5 h-5" /></Link>
              <Link to="/rank" className="btn-secondary">Back to MarqiRank Overview</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RankAeoPage;
